import {Fragment} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {CheckIcon, KeyIcon} from '@heroicons/react/24/outline';
import PasswordChecklist from 'react-password-checklist';
import {Button} from '../../components/Button';
import {useChangePasswordModal} from '../../hooks/auth/useChangePasswordModal';

export interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

export default function ChangePasswordModal({
  isOpen,
  onClose,
  userEmail,
}: ChangePasswordModalProps) {
  // All business logic is now in the hook
  const {
    step,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordValid,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    setPasswordValid,
    errorMessage,
    handleChangePassword,
    resetModal,
    isProcessing,
  } = useChangePasswordModal();

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleSubmit = async () => {
    await handleChangePassword(userEmail);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-surface px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div
                    className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${
                      step === 'success' ? 'bg-success-subtle' : 'bg-subtle'
                    }`}
                  >
                    {step === 'success' ? (
                      <CheckIcon className="h-8 w-8 text-success" aria-hidden="true" />
                    ) : (
                      <KeyIcon className="h-8 w-8 text-brand" aria-hidden="true" />
                    )}
                  </div>
                  <div className="mt-4 text-center sm:mt-6">
                    <Dialog.Title as="h3" className="text-lg  leading-6 text-main">
                      {step === 'success' ? 'Password Changed' : 'Change Your Password'}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm  text-muted">
                        {step === 'success'
                          ? 'Your password has been successfully changed. Your new password is now active.'
                          : 'Enter your current password and choose a new password.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form Step */}
                {step === 'form' && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label
                        htmlFor="current-password"
                        className="block text-sm  text-main"
                      >
                        Current Password
                      </label>
                      <div className="mt-2">
                        <input
                          id="current-password"
                          type="password"
                          autoComplete="current-password"
                          required
                          disabled={isProcessing}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="new-password" className="block text-sm  text-main">
                        New Password
                      </label>
                      <div className="mt-2">
                        <input
                          id="new-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          disabled={isProcessing}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className={`block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all ${
                            newPassword.length === 0 || passwordValid
                              ? ''
                              : 'ring-red-300 focus:ring-red-500 bg-error-subtle'
                          }`}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-new-password"
                        className="block text-sm  text-main"
                      >
                        Confirm New Password
                      </label>
                      <div className="mt-2">
                        <input
                          id="confirm-new-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          disabled={isProcessing}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all ${
                            newPassword.length === 0 || passwordValid
                              ? ''
                              : 'ring-red-300 focus:ring-red-500 bg-error-subtle'
                          }`}
                        />
                      </div>
                    </div>

                    {newPassword.length > 0 && (
                      <div className="mt-4">
                        <PasswordChecklist
                          rules={[
                            'minLength',
                            'specialChar',
                            'number',
                            'capital',
                            'lowercase',
                            'match',
                          ]}
                          minLength={10}
                          value={newPassword}
                          valueAgain={confirmPassword}
                          onChange={setPasswordValid}
                          style={{ fontSize: '0.875rem' }}
                          className=" text-muted"
                        />
                      </div>
                    )}

                    {errorMessage && <p className="text-sm  text-error">{errorMessage}</p>}

                    <Button
                      type="button"
                      variant="primary"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={isProcessing || !passwordValid || !currentPassword}
                      loading={isProcessing}
                    >
                      {isProcessing ? 'Changing Password...' : 'Change Password'}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      fullWidth
                      onClick={handleClose}
                      disabled={isProcessing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                {/* Success Step */}
                {step === 'success' && (
                  <div className="mt-6 sm:mt-8">
                    <Button type="button" variant="primary" fullWidth onClick={handleClose}>
                      Done
                    </Button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
